import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { counterpartyService } from '../services/counterpartyService';
import { invoiceService } from '../services/invoiceService';
import { expenseService } from '../services/expenseService';
import { contractService } from '../services/contractService';
import { DatePickerWithRange } from '../components/ui/date-range-picker';
import { InvoiceTable } from '../components/InvoiceTable';
import MultiStepInvoiceModal from '../components/MultiStepInvoiceModal';
import EditInvoiceModal from '../components/EditInvoiceModal';
import PdfViewer from '../components/PdfViewer';
import DownloadLanguageModal from '../components/DownloadLanguageModal';
import { Language } from '../types/language';
import { useTranslation } from '../hooks/useTranslation';
import { useLanguage } from '../contexts/LanguageContext';
import { codeToName, codeToNameRu } from '../services/countryService';

// Компонент для отображения названия страны по коду
const CountryDisplay: React.FC<{ countryCode: string; language: Language }> = ({ countryCode, language }) => {
  const { t } = useTranslation();
  const [countryName, setCountryName] = useState<string>(countryCode);

  useEffect(() => {
    const loadCountryName = async () => {
      try {
        if (language === Language.RU) {
          const name = await codeToNameRu(countryCode);
          setCountryName(name);
        } else {
          const name = await codeToName(countryCode, language);
          setCountryName(name);
        }
      } catch (error) {
        console.error('Error loading country name:', error);
        setCountryName(countryCode);
      }
    };
    loadCountryName();
  }, [countryCode, language]);

  return (
    <div>
      <label className="text-sm font-medium text-gray-600">{t('counterparty.country')}</label>
      <p className="text-lg">{countryName}</p>
    </div>
  );
};

export default function CounterpartyDetails() {
  const { companyId, counterpartyId } = useParams<{ companyId: string; counterpartyId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const getExpenseStatusLabel = (status: string) =>
    t(`companies.expenses.statuses.${status}`) || status;
  const getContractStatusLabel = (status: string) =>
    t(`companies.contracts.statuses.${status}`) || status;
  
  // Infinite scroll
  const pageSize = 25;
  
  // Фильтры
  const [dateRange, setDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({
    from: undefined,
    to: undefined
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'invoices' | 'transactions' | 'expenses' | 'contracts'>('invoices');

  // Модальные окна
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [copyFromInvoice, setCopyFromInvoice] = useState<any>(null);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [downloadLanguageModalOpen, setDownloadLanguageModalOpen] = useState(false);
  const [invoiceToDownload, setInvoiceToDownload] = useState<any>(null);

  // Загрузка контрагента
  const { data: counterparty, isLoading: counterpartyLoading } = useQuery({
    queryKey: ['counterparty', companyId, counterpartyId],
    queryFn: () => counterpartyService.getCounterparty(companyId!, counterpartyId!),
    enabled: !!companyId && !!counterpartyId
  });

  // Загрузка инвойсов с infinite scroll
  const { 
    data: invoicesData, 
    isLoading: invoicesLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery({
    queryKey: ['counterparty-invoices', companyId, counterpartyId, dateRange, searchTerm],
    queryFn: ({ pageParam = 0 }) => {
      const params = new URLSearchParams({
        page: pageParam.toString(),
        size: pageSize.toString(),
      });

      if (dateRange.from) {
        params.append('startDate', dateRange.from.toISOString().split('T')[0]);
      }
      if (dateRange.to) {
        params.append('endDate', dateRange.to.toISOString().split('T')[0]);
      }

      return invoiceService.getCounterpartyInvoicesPaginated(companyId!, counterpartyId!, params);
    },
    enabled: !!companyId && !!counterpartyId,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.content.length < pageSize) {
        return undefined; // Нет следующей страницы
      }
      return allPages.length; // Следующая страница
    },
    initialPageParam: 0,
  });

  const { data: relatedExpenses } = useQuery({
    queryKey: ['counterparty-expenses', companyId, counterpartyId],
    queryFn: () => {
      const params = new URLSearchParams({
        page: '0',
        size: '5',
        counterpartyId: counterpartyId!
      });
      return expenseService.getPaginated(companyId!, params);
    },
    enabled: !!companyId && !!counterpartyId
  });

  const { data: relatedContracts } = useQuery({
    queryKey: ['counterparty-contracts', companyId, counterpartyId],
    queryFn: () => {
      const params = new URLSearchParams({
        page: '0',
        size: '5',
        counterpartyId: counterpartyId!
      });
      return contractService.getPaginated(companyId!, params);
    },
    enabled: !!companyId && !!counterpartyId
  });

  // Обработчик скролла для infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      // Проверяем, достигли ли мы конца страницы (с небольшим отступом в 200px)
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      if (scrollTop + windowHeight >= documentHeight - 200) {
        if (hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleDateRangeChange = (range: { from: Date | undefined; to: Date | undefined }) => {
    setDateRange(range);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const clearFilters = () => {
    setDateRange({ from: undefined, to: undefined });
    setSearchTerm('');
  };

  // Функции для работы с инвойсами
  const handleDownloadClick = (invoice: any) => {
    setInvoiceToDownload(invoice);
    setDownloadLanguageModalOpen(true);
  };

  const refetchInvoices = () => {
    queryClient.invalidateQueries({ queryKey: ['counterparty-invoices', companyId, counterpartyId] });
  };

  if (counterpartyLoading) {
    return (
      <div className="max-w-app mx-auto px-6 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  if (!counterparty) {
    return (
      <div className="max-w-app mx-auto px-6 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">{t('counterparty.details.notFound')}</h1>
          <button 
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            ← {t('common.back')}
          </button>
        </div>
      </div>
    );
  }

  // Объединяем все страницы в один массив
  const invoices = invoicesData?.pages.flatMap(page => page.content) || [];
  const totalElements = invoicesData?.pages[0]?.totalElements || 0;
  
  // Фильтрация на клиенте для поиска
  const filteredInvoices = invoices.filter((invoice: any) => {
    const searchLower = searchTerm.toLowerCase();
    
    // Поиск по сумме (преобразуем в строку без форматирования для поиска)
    // Убираем точки и запятые из суммы и поискового запроса для корректного поиска
    const totalAmountStr = invoice.totalAmount != null 
      ? invoice.totalAmount.toString().replace(/[.,]/g, '') 
      : '';
    const searchAmountStr = searchTerm.replace(/[.,]/g, '');
    const matchesAmount = searchAmountStr !== '' && totalAmountStr.startsWith(searchAmountStr);
    
    return searchTerm === '' || 
           invoice.invoiceNumber.toLowerCase().includes(searchLower) ||
           (invoice.items && invoice.items.some((item: any) => item.description.toLowerCase().includes(searchLower))) ||
           (invoice.notes && invoice.notes.toLowerCase().includes(searchLower)) ||
           (invoice.internalComment && invoice.internalComment.toLowerCase().includes(searchLower)) ||
           matchesAmount;
  });

  return (
    <div className="max-w-app mx-auto px-6 py-8 space-y-6">
      {/* Заголовок */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button 
            onClick={() => navigate(-1)}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            ← {t('common.back')}
          </button>
          <div>
            <h1 className="text-3xl font-bold">{counterparty.name}</h1>
            <p className="text-gray-600">
              {counterparty.taxId && `${t('counterparty.taxId')}: ${counterparty.taxId}`}
              {counterparty.vatId && ` • ${t('counterparty.vatId')}: ${counterparty.vatId}`}
            </p>
          </div>
        </div>
      </div>

      {/* Информация о контрагенте */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">{t('counterparty.details.title')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-600">{t('counterparty.name')}</label>
            <p className="text-lg">{counterparty.name}</p>
          </div>
          {counterparty.taxId && (
            <div>
              <label className="text-sm font-medium text-gray-600">{t('counterparty.taxId')}</label>
              <p className="text-lg">{counterparty.taxId}</p>
            </div>
          )}
          {counterparty.vatId && (
            <div>
              <label className="text-sm font-medium text-gray-600">{t('counterparty.vatId')}</label>
              <p className="text-lg">{counterparty.vatId}</p>
            </div>
          )}
          {counterparty.country && (
            <CountryDisplay countryCode={counterparty.country} language={language} />
          )}
          {counterparty.city && (
            <div>
              <label className="text-sm font-medium text-gray-600">{t('counterparty.city')}</label>
              <p className="text-lg">{counterparty.city}</p>
            </div>
          )}
          {counterparty.address && (
            <div>
              <label className="text-sm font-medium text-gray-600">{t('counterparty.address')}</label>
              <p className="text-lg">{counterparty.address}</p>
            </div>
          )}
          {counterparty.contactPerson && (
            <div>
              <label className="text-sm font-medium text-gray-600">{t('counterparty.contactPerson')}</label>
              <p className="text-lg">{counterparty.contactPerson}</p>
            </div>
          )}
          {counterparty.email && (
            <div>
              <label className="text-sm font-medium text-gray-600">{t('counterparty.email')}</label>
              <p className="text-lg">{counterparty.email}</p>
            </div>
          )}
          {counterparty.phone && (
            <div>
              <label className="text-sm font-medium text-gray-600">{t('counterparty.phone')}</label>
              <p className="text-lg">{counterparty.phone}</p>
            </div>
          )}
          {counterparty.comment && (
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-gray-600">{t('counterparty.comment') || 'Комментарий'}</label>
              <p className="text-lg whitespace-pre-wrap">{counterparty.comment}</p>
            </div>
          )}
        </div>
      </div>

      {/* Табы */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6 overflow-x-auto">
            <button
              onClick={() => setActiveTab('invoices')}
              className={`py-4 px-1 border-b-2 font-medium whitespace-nowrap ${
                activeTab === 'invoices'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('companies.invoices.title')} ({totalElements})
            </button>
            <button
              onClick={() => setActiveTab('transactions')}
              className={`py-4 px-1 border-b-2 font-medium whitespace-nowrap ${
                activeTab === 'transactions'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('companies.transactions.title')}
            </button>
            <button
              onClick={() => setActiveTab('expenses')}
              className={`py-4 px-1 border-b-2 font-medium whitespace-nowrap ${
                activeTab === 'expenses'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('companies.tabs.expenses') || 'Расходы'} ({relatedExpenses?.totalElements ?? 0})
            </button>
            <button
              onClick={() => setActiveTab('contracts')}
              className={`py-4 px-1 border-b-2 font-medium whitespace-nowrap ${
                activeTab === 'contracts'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('companies.tabs.contracts') || 'Контракты'} ({relatedContracts?.totalElements ?? 0})
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'invoices' && (
            <>
              {/* Фильтры */}
              <div className="mb-6 bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-4">{t('common.filters')}</h3>
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <label className="text-sm font-medium mb-2 block">{t('common.search')}</label>
                    <input
                      type="text"
                      placeholder={t('companies.invoices.searchPlaceholder')}
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-sm font-medium mb-2 block">{t('common.period')}</label>
                    <DatePickerWithRange
                      date={dateRange}
                      onDateChange={handleDateRangeChange}
                    />
                  </div>
                  <div className="flex items-end">
                    <button 
                      onClick={clearFilters}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      {t('common.clear')}
                    </button>
                  </div>
                </div>
              </div>

              {/* Список инвойсов */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">{t('counterparty.details.invoicesTitle')}</h3>
                  <button
                    onClick={() => setIsAddModalOpen(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    + {t('invoices.createInvoice')}
                  </button>
                </div>
                {invoicesLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="text-lg">{t('counterparty.details.loadingInvoices')}</div>
                  </div>
                ) : filteredInvoices.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-600">{t('counterparty.details.noInvoices')}</p>
                  </div>
                ) : (
                  <>
                    <InvoiceTable 
                      invoices={filteredInvoices}
                      companyId={companyId}
                      onView={(invoice) => {
                        setSelectedInvoice(invoice);
                        setPdfViewerOpen(true);
                      }}
                      onEdit={(invoice) => {
                        setSelectedInvoice(invoice);
                        setIsEditModalOpen(true);
                      }}
                      onDownload={handleDownloadClick}
                      onCopy={(invoice) => {
                        setCopyFromInvoice(invoice);
                        setIsAddModalOpen(true);
                      }}
                      onStatusChange={async (invoiceId, newStatus) => {
                        try {
                          await invoiceService.updateInvoiceStatus(invoiceId, newStatus);
                          refetchInvoices();
                        } catch (error) {
                          console.error('Error updating invoice status:', error);
                        }
                      }}
                      showClient={false}
                    />
                    
                    {isFetchingNextPage && (
                      <div className="mt-4 text-center">
                        <div className="text-gray-600">{t('common.loading')}</div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {activeTab === 'transactions' && (
            <div className="text-center py-10 text-gray-600">
              {t('common.comingSoon') || 'Скоро'}
            </div>
          )}

          {activeTab === 'expenses' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">
                  {t('companies.tabs.expenses') || 'Расходы'}
                </h3>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      sessionStorage.setItem(`preselect_expense_counterparty_${companyId}`, counterpartyId!);
                      navigate(`/companies/${companyId}#expenses`);
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {t('companies.expenses.add') || 'Добавить расход'}
                  </button>
                  <button
                    onClick={() => {
                      sessionStorage.setItem(`preselect_expense_counterparty_${companyId}`, counterpartyId!);
                      navigate(`/companies/${companyId}#expenses`);
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {t('common.viewAll') || 'Смотреть все'}
                  </button>
                </div>
              </div>
              {(relatedExpenses?.content || []).length === 0 ? (
                <p className="text-sm text-gray-600">{t('companies.expenses.empty') || 'Расходы не найдены'}</p>
              ) : (
                <div className="space-y-2">
                  {(relatedExpenses?.content || []).map((expense: any) => (
                    <div key={expense.id} className="flex items-center justify-between border border-gray-100 rounded p-2">
                      <div>
                        <p className="font-medium text-sm">{expense.documentNumber}</p>
                        <p className="text-xs text-gray-500">{expense.issueDate}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm">{expense.grossAmount ?? '-'} {expense.currency}</p>
                        <p className="text-xs text-gray-500">{getExpenseStatusLabel(expense.status)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'contracts' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">
                  {t('companies.tabs.contracts') || 'Контракты'}
                </h3>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      sessionStorage.setItem(`preselect_contract_counterparty_${companyId}`, counterpartyId!);
                      navigate(`/companies/${companyId}#contracts`);
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {t('companies.contracts.add') || 'Добавить контракт'}
                  </button>
                  <button
                    onClick={() => {
                      sessionStorage.setItem(`preselect_contract_counterparty_${companyId}`, counterpartyId!);
                      navigate(`/companies/${companyId}#contracts`);
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {t('common.viewAll') || 'Смотреть все'}
                  </button>
                </div>
              </div>
              {(relatedContracts?.content || []).length === 0 ? (
                <p className="text-sm text-gray-600">{t('companies.contracts.empty') || 'Контракты не найдены'}</p>
              ) : (
                <div className="space-y-2">
                  {(relatedContracts?.content || []).map((contract: any) => (
                    <div key={contract.id} className="flex items-center justify-between border border-gray-100 rounded p-2">
                      <div>
                        <p className="font-medium text-sm">{contract.contractNumber}</p>
                        <p className="text-xs text-gray-500">{contract.title}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">
                          {getContractStatusLabel(contract.status)}
                        </p>
                        <p className="text-xs text-gray-500">{contract.startDate || '-'} - {contract.endDate || '-'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Модальные окна */}
      <MultiStepInvoiceModal
        companyId={companyId!}
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setCopyFromInvoice(null);
          refetchInvoices(); // Обновляем данные при закрытии
        }}
        onSuccess={() => {
          setIsAddModalOpen(false);
          setCopyFromInvoice(null);
          refetchInvoices();
        }}
        copyFromInvoice={copyFromInvoice}
        preselectedClientId={copyFromInvoice ? undefined : counterpartyId}
      />

      <EditInvoiceModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedInvoice(null);
          refetchInvoices(); // Обновляем данные при закрытии
        }}
        invoice={selectedInvoice}
        companyId={companyId!}
        onSuccess={() => {
          setIsEditModalOpen(false);
          setSelectedInvoice(null);
          refetchInvoices();
        }}
        onInvoiceUpdated={() => {
          refetchInvoices(); // Обновляем данные после редактирования
        }}
      />

      <PdfViewer
        isOpen={pdfViewerOpen}
        onClose={() => {
          setPdfViewerOpen(false);
          setSelectedInvoice(null);
        }}
        companyId={companyId!}
        invoiceId={selectedInvoice?.id || ''}
        invoiceNumber={selectedInvoice?.invoiceNumber || ''}
        clientName={selectedInvoice?.clientName || ''}
        pdfLanguage={selectedInvoice?.pdfLanguage}
      />

      <DownloadLanguageModal
        isOpen={downloadLanguageModalOpen}
        onClose={() => {
          setDownloadLanguageModalOpen(false);
          setInvoiceToDownload(null);
        }}
        onConfirm={(language: Language) => {
          if (invoiceToDownload) {
            const link = document.createElement('a');
            link.href = `/api/companies/${companyId}/invoices/${invoiceToDownload.id}/pdf?language=${language}`;
            // Очищаем имя клиента от недопустимых символов для имени файла
            const cleanClientName = invoiceToDownload.clientName.replace(/[^a-zA-Z0-9а-яА-Я\s-]/g, '').replace(/\s+/g, '-');
            const langSuffix = language === Language.PL_ONLY ? 'pl' : language === Language.PL_EN ? 'plen' : 'en';
            link.download = `${invoiceToDownload.invoiceNumber}-${cleanClientName}-${langSuffix}.pdf`;
            link.click();
          }
          setDownloadLanguageModalOpen(false);
          setInvoiceToDownload(null);
        }}
        currentLanguage={(invoiceToDownload?.pdfLanguage as Language) || language}
        invoiceNumber={invoiceToDownload?.invoiceNumber || ''}
      />
    </div>
  );
}
